import {
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_PARTNERS,
  GPX_ROUTES,
  GPX_EVENT_NAME,
  type GPXEventType,
  type GPXFeature,
  type GPXCapability,
  type GPXPartner,
  type GPXRoute,
  type GPXEventName,
} from './constants/trackingEnums';
import { normalizeUserInfo, normalizeAccountInfo } from './normalizers';
import type {
  UserData,
  AccountData,
  DataProvider,
  NormalizedUserInfo,
  NormalizedAccountInfo,
} from './types';
import {
  determineGSPXTrackingID,
  defaultCaptureError,
  preserveUndefined,
} from './utils';
import {
  validateEnumValue,
  validateRequiredField,
  validateTimestamp,
  validateEccAccountIDs,
  validateObject,
} from './validators';

// Configuration interface for initialization
export interface GainsightConfig {
  captureError?: (error: Error, context?: Record<string, unknown>) => void;
}

// Parameters interface for trackEvent method
export interface TrackEventParams {
  name: string;
  type: GPXEventType;
  feature: GPXFeature;
  capability: GPXCapability;
  route: GPXRoute;
  partner?: GPXPartner;
  entities?: Record<string, unknown>;
  additionalKeys?: Record<string, unknown>;
  timestamp?: number;
  userID?: string;
  eccAccountIDs?: Record<string, string>;
  gainsightEventName?: GPXEventName;
}

// Extend the Window interface to include aptrinsic
declare global {
  interface Window {
    // more info at https://support.gainsight.com/PX/API_for_Developers/02Usage_of_Different_APIs/Use_Custom_Event_API
    aptrinsic?: (method: 'identify' | 'track', ...args: unknown[]) => void;
  }
}

/**
 * Main class for Gainsight PX integration
 */
class CapstoneGainSightPX {
  public dataProvider: DataProvider | null;
  public trackingID: string;
  public captureError: (
    error: Error,
    context?: Record<string, unknown>,
  ) => void;

  constructor() {
    this.dataProvider = null;
    this.trackingID = determineGSPXTrackingID();
    this.captureError = defaultCaptureError;
  }

  /**
   * Identifies a user and account in the Gainsight PX platform and updates the last identified timestamp.
   * Note: This method expects normalized data from normalizeUserInfo and normalizeAccountInfo.
   * Do not pass raw user/account data directly.
   *
   * @see normalizeUserInfo
   * @see normalizeAccountInfo
   */
  addIdentifier(
    userInfo?: NormalizedUserInfo,
    accountInfo?: NormalizedAccountInfo,
  ): void {
    if (window.aptrinsic) {
      window.aptrinsic('identify', userInfo || {}, accountInfo || {});
    }
  }

  /**
   * Identifies the current user in Gainsight PX
   */
  identify(): void {
    try {
      if (!window.aptrinsic) {
        throw new Error('Tracking service not initialized');
      }

      if (!this.dataProvider) {
        throw new Error('Data provider not initialized');
      }

      const userData = this.dataProvider.getUserData();
      const accountData = this.dataProvider.getAccountData();

      if (!userData || !accountData) {
        throw new Error('Required user or account data not available');
      }

      // Get normalized data using class methods that handle trackingID
      const userInfo = this.normalizeUserData(userData);
      const accountInfo = this.normalizeAccountData(accountData);

      this.addIdentifier(userInfo, accountInfo);
    } catch (error) {
      this.captureError(new Error('Failed to identify user'), {
        error,
        context: {
          hasDataProvider: !!this.dataProvider,
          hasUserData: !!this.dataProvider?.getUserData(),
          hasAccountData: !!this.dataProvider?.getAccountData(),
        },
      });
    }
  }

  /**
   * Normalizes user data by adding environment-specific tracking ID
   */
  normalizeUserData(userData: UserData): NormalizedUserInfo | undefined {
    try {
      const normalizedUserInfo = normalizeUserInfo(userData, this.trackingID);
      return normalizedUserInfo;
    } catch (error) {
      this.captureError(new Error('Failed to normalize user data'), {
        error,
        context: {
          userData,
        },
      });
      return undefined;
    }
  }

  /**
   * Normalizes account data
   */
  normalizeAccountData(
    accountData: AccountData,
  ): NormalizedAccountInfo | undefined {
    try {
      const normalizedAccountInfo = normalizeAccountInfo(accountData);
      return normalizedAccountInfo;
    } catch (error) {
      this.captureError(new Error('Failed to normalize account data'), {
        error,
        context: {
          accountData,
        },
      });
      return undefined;
    }
  }

  /**
   * Initializes the Gainsight PX tracking instance with a data provider and optional configuration.
   */
  initialize(dataProvider: DataProvider, config: GainsightConfig = {}): void {
    if (
      !dataProvider ||
      typeof dataProvider.getUserData !== 'function' ||
      typeof dataProvider.getAccountData !== 'function' ||
      typeof dataProvider.getPSAPData !== 'function' ||
      typeof dataProvider.getSelectedCallData !== 'function'
    ) {
      this.captureError(
        new Error(
          'Invalid data provider: must implement getUserData, getAccountData, getPSAPData, and getSelectedCallData methods',
        ),
      );
    }

    this.dataProvider = dataProvider;

    if (config.captureError) {
      if (typeof config.captureError !== 'function') {
        this.captureError(
          new Error(
            'Invalid error capture function provided: must be a function',
          ),
        );
      } else {
        this.captureError = config.captureError;
      }
    }
  }

  /**
   * Tracks a custom event in GainSight PX analytics platform.
   */
  trackEvent(params: TrackEventParams): void {
    const {
      name,
      type,
      feature,
      capability,
      route,
      partner,
      entities,
      additionalKeys,
      timestamp,
      userID,
      eccAccountIDs,
      gainsightEventName = GPX_EVENT_NAME.UNITE_USAGE,
    } = params;

    try {
      // Check if tracking and data provider are available
      if (!window.aptrinsic) {
        throw new Error('Tracking service not initialized');
      }

      if (!this.dataProvider) {
        throw new Error(
          'Data provider not initialized. Call initialize() first',
        );
      }

      // Validate required parameters are present
      validateRequiredField(name, 'Event name');
      validateRequiredField(type, 'Event type');
      validateRequiredField(feature, 'Feature');
      validateRequiredField(capability, 'Capability');
      validateRequiredField(route, 'Route');

      // Validate enum values
      validateEnumValue(type, GPX_EVENT_TYPES, 'Event type');
      validateEnumValue(feature, GPX_FEATURES, 'Feature');
      validateEnumValue(capability, GPX_CAPABILITIES, 'Capability');
      validateEnumValue(route, GPX_ROUTES, 'Route');
      validateEnumValue(
        gainsightEventName,
        GPX_EVENT_NAME,
        'Gainsight event name',
      );

      if (partner) {
        validateEnumValue(partner, GPX_PARTNERS, 'Partner');
      }

      // Validate timestamp
      validateTimestamp(timestamp);

      // Get data from provider
      const userData = this.dataProvider.getUserData();
      const psapData = this.dataProvider.getPSAPData();
      const callData = this.dataProvider.getSelectedCallData();

      const storedUserID = userData?.profileId;
      const psapID = psapData?.accountId;
      const payloadID = callData?.payloadId;
      const callStartTime = callData?.callStartTime;

      // Validate passed entities if present and not null
      if (entities != null) {
        validateObject(entities, 'entities');
      }

      // Prepare user ID, session ID, ECC account IDs, additional keys and entities
      const finalUserID = userID ?? storedUserID;
      const finalEccAccountIDs =
        eccAccountIDs ?? (psapID ? { source: psapID } : {});
      const finalAdditionalKeys = {
        ...(additionalKeys ?? {}),
        screen_width: window.innerWidth,
        screen_height: window.innerHeight,
      };
      const finalEntities = {
        ...(payloadID && { payload_id: payloadID }),
        ...(callStartTime && { call_start_time: callStartTime }),
        ...(entities ?? {}),
      };

      // Validate ECC Account IDs
      validateEccAccountIDs(finalEccAccountIDs);

      // Validate additional keys and entities
      validateObject(finalAdditionalKeys, 'additionalKeys');
      validateObject(finalEntities, 'entities');

      // Construct event info with current timestamp
      const eventInfo = {
        event_name: name,
        event_type: type,
        feature,
        capability,
        route,
        event_timestamp: timestamp ?? Date.now(),
        ...(partner && { partner }),
        ...(finalUserID && { user_id: finalUserID }),
        ecc_account_ids: preserveUndefined(finalEccAccountIDs),
        additional_keys: preserveUndefined(finalAdditionalKeys),
        entities: preserveUndefined(finalEntities),
      };

      window.aptrinsic('track', gainsightEventName, eventInfo);
    } catch (error) {
      this.captureError(
        new Error(`Failed to track GPX event: ${(error as Error).message}`),
        {
          error,
          context: {
            name,
            type,
            feature,
            capability,
            route,
            timestamp,
          },
        },
      );
    }
  }
}

/**
 * NOTE This returns a singleton of CapstoneGainSightPX
 */
export default new CapstoneGainSightPX();
